import { SEARCH_META } from "@/apollo/queries/search";
import { SEARCH_CONFIG } from "@/apollo/queries/searchConfig";
import SearchPageMetaTags, {
  tags,
} from "@/components/MetaTags/SearchPageMetaTags";
import PseudoCanvas from "@/components/PseudoCanvas";
import SearchNoResults from "@/components/SearchNoResults";
import { useBreadcrumbs, useSearchConfigQuery } from "@/hooks";
import config, { getStoreId } from "@/lib/config";
import { SLIDER_MAX_NUM } from "@/lib/constants";
import routes from "@/lib/routes";
import withMiddlewares from "@/middlewares/withMiddlewares";
import contentsStyles from "@/styles/Contents.module.scss";
import { useRouter } from "next/router";
import { useEffect, useMemo } from "react";

export default function Search(props) {
  const router = useRouter();
  const searchQuery = router?.query?.q;
  const { setBreadcrumbs } = useBreadcrumbs();
  const res = useSearchConfigQuery({ fetchPolicy: "cache-first" });
  const searchConfig = res?.data?.searchConfig;
  const isShowLivePalette = searchConfig?.livePaletteKeywords?.includes(
    searchQuery
  );
  const hitWords = searchConfig?.keywordMinScore?.filter((keywordWithScore) => {
    const { keyword } = keywordWithScore;
    return keyword === searchQuery;
  });
  const minScoreValue =
    hitWords?.length > 0 ? hitWords[0]?.score : searchConfig?.defaultMinScore;
  const minScore = minScoreValue;
  const schemas = useMemo(() => {
    const schemas = [
      {
        id: "search-channel",
        type: "palette",
        viewType: "slider",
        header: {
          text: "月額商品",
          link: {
            href: routes.search(searchQuery, "channel"),
          },
        },
        graphql: {
          query: SEARCH_META,
          options: {
            variables: {
              keyword: searchQuery,
              filter: {
                not: {
                  "values.channel": [getStoreId(), config.valuePlanId.vod],
                },
              },
              sort: "rel",
              offset: 0,
              limit: SLIDER_MAX_NUM,
              minScore,
            },
          },
          path: "search",
        },
      },
      {
        id: "search-store",
        type: "palette",
        viewType: "slider",
        header: {
          text: "単品商品",
          link: {
            href: routes.search(searchQuery, "store"),
          },
        },
        graphql: {
          query: SEARCH_META,
          options: {
            variables: {
              keyword: searchQuery,
              filter: {
                "values.channel": getStoreId(),
              },
              sort: "rel",
              offset: 0,
              limit: SLIDER_MAX_NUM,
              minScore,
            },
          },
          path: "search",
        },
      },
    ];

    schemas.unshift(
      isShowLivePalette && {
        id: "search-live",
        type: "palette",
        viewType: "slider",
        header: {
          text: "ライブ",
          link: {
            href: routes.search(searchQuery, "live"),
          },
        },
        graphql: {
          query: SEARCH_META,
          options: {
            variables: {
              keyword: searchQuery,
              filter: {
                "values.is_live": true,
                time_range: { "values.encode_end_date": { gte: "now" } },
              },
              sort: "rel",
              offset: 0,
              limit: SLIDER_MAX_NUM,
              minScore,
            },
          },
          path: "search",
        },
      }
    );

    return schemas;
  }, [searchQuery]);

  useEffect(() => {
    setBreadcrumbs([
      { text: "ホーム", href: routes.home },
      { text: "検索結果" },
    ]);
  }, [searchQuery]);

  return (
    <>
      <SearchPageMetaTags keyword={searchQuery} />
      <div className={contentsStyles.container}>
        {searchQuery && (
          <>
            <header>
              <h1>{searchQuery}の検索結果</h1>
            </header>
            <div>
              <PseudoCanvas
                schemas={schemas}
                noContent={<SearchNoResults searchQuery={searchQuery} />}
              />
            </div>
          </>
        )}
        {!searchQuery && <SearchNoResults searchQuery={searchQuery} />}
      </div>
    </>
  );
}

export const getServerSideProps = withMiddlewares(
  async ({ req, query }, { serverProps, error, apolloClient }) => {
    const metatags = tags(query.q, null);
    await apolloClient.query({
      query: SEARCH_CONFIG,
      fetchPolicy: "cache-first",
    });
    return {
      props: {
        ...serverProps,
        error,
        metatags,
        initialApolloState: apolloClient.cache.extract(),
      },
    };
  }
);
